import { each, filter, isNumber, keys, map } from "lodash";

import { 
    setupFormCommonBehaviour,
} from ".";

import { 
    InputNameConfigMap,
    FileUploadItemError,
    uploadAttachments,
} from "./file-upload";

import { initCheckboxToggle } from "../checkbox-toggle";

declare const jQuery: any;
const $ = jQuery;

const handlerName: string = "supp-acc-attachment"; // PHP FileUpload class NAME const

const ajaxCall: Function = window["iw"].utils.ajaxCall;
const errorModal: Function = window["iw"].modal.errorModal;
const confirmModal: Function = window["iw"].modal.confirmModal;
const successModal: Function = window["iw"].modal.successModal;
const waitModal: Function = window["iw"].modal.waitModal;
const initCollapsibleSections: Function = window["iw"].initCollapsibleSections;
const reinitWysiwygs: Function = window["iw"].reinitWysiwygs;
const scrollToWithHeaderOffset: Function = window["iw"].layout.scrollToWithHeaderOffset;
const initCustomCheckboxes: Function = window["iw"].initCustomCheckboxes;
const initCustomSelects: Function = window["iw"].initCustomSelects;

export function initEditSupplier($main): void
{
    const $mainInner = $main.children(`[data-edit-supplier]`);
    if ($mainInner.length === 0)
    {
        return;
    }

    const supplierId: number = parseInt($mainInner.attr("data-edit-supplier"), 10);

    if (isNaN(supplierId))
    {
        throw new Error(`No supplierId`);
    }

    const previewUrl: string = $mainInner.attr("data-preview-url");
    const approvalRequested: boolean = !!$mainInner.attr("data-approval-requested");
    
    initMainInner($mainInner, supplierId, previewUrl, approvalRequested);
}

function initMainInner($mainInner, supplierId: number, previewUrl: string, approvalRequested: boolean): void
{
    const $save = $mainInner.find("[data-main-buttons] [data-save]");
    const $preview = $mainInner.find("[data-main-buttons] [data-preview]");
    const $publish = $mainInner.find("[data-main-buttons] [data-publish]");

    let unsavedChanges = false;

    const onFirstChange = () => {
        $save.removeAttr("disabled");
        $publish.removeAttr("disabled");
        $preview.attr("disabled", "disabled");
        unsavedChanges = true;
    };

    const commonCtrls = setupFormCommonBehaviour($mainInner, onFirstChange);

    // 4-stage server request process:
    // (1) Create supplier post (if not yet created) and update non-file-related post data
    // (2) Upload file attachments
    // (3) Update supplier post with secondary (file-related) post data
    // (4) Get updated form HTML and render/initialize
    
    const buildBasicAjaxData = (rawData, requestApproval: boolean) => {
        return {
            request_approval: requestApproval,
            supplier_id: supplierId,
            supplier_title: rawData.supplier_title,
            supplier_main_product_service: rawData.supplier_main_product_service,
            supplier_web_page_url: rawData.supplier_web_page_url,
            supplier_email: rawData.supplier_email,
            supplier_address: rawData.supplier_address,
            supplier_facebook_url: rawData.supplier_facebook_url,
            supplier_linkedin_url: rawData.supplier_linkedin_url,
            supplier_pinterest_url: rawData.supplier_pinterest_url,
            supplier_twitter_url: rawData.supplier_twitter_url,
            supplier_instagram_url: rawData.supplier_instagram_url,
            supplier_countries: rawData.supplier_countries,
            supplier_categories: rawData.supplier_categories,
            supplier_overview: rawData.supplier_overview,
            supplier_details: rawData.supplier_details,
            supplier_videos: rawData.supplier_videos
        };
    };

    const saveBasicAjaxData = async (basicAjaxData): Promise<{ supplierId: number }> => {
        waitModal(`Submitting supplier profile, please wait...`);
        const response = await ajaxCall("supp-acc-update-supplier", basicAjaxData);
        if (!response?.supplierId) {
            console.error(`saveBasicAjaxData - bad response:`, response);
            throw new Error(`No response.supplierId`);
        }
        return response;
    };

    const uploadSupplierPostAttachments = async (rawData, newSupplierId: number): Promise<void> => {
        const uploadData = { supplier_id: newSupplierId };
        const inputNameConfigMap: InputNameConfigMap = {
            supplier_logo_image: { inputType: "images", progressMsg: `Uploading logo image...` },
            supplier_overview_image: { inputType: "images", progressMsg: `Uploading overview image...` },
            supplier_details_image: { inputType: "images", progressMsg: `Uploading details image...` },
            supplier_banner_image: { inputType: "images", progressMsg: `Uploading banner image...` },
            supplier_images: { inputType: "images", progressMsg: `Uploading photos...` },
            supplier_documents: { inputType: "documents", progressMsg: `Uploading documents...` }
        };
        await uploadAttachments(handlerName, rawData, inputNameConfigMap, uploadData);
    };

    const buildSecondaryAjaxData = (rawData, newSupplierId: number) => {
        return {
            supplier_id: newSupplierId,
            supplier_logo_image: rawData.supplier_logo_image,
            supplier_overview_image: rawData.supplier_overview_image,
            supplier_details_image: rawData.supplier_details_image,
            supplier_banner_image: rawData.supplier_banner_image,
            supplier_images: rawData.supplier_images,
            supplier_documents: rawData.supplier_documents
        };
    };

    const saveSecondaryAjaxData = async (secondaryAjaxData): Promise<{ supplierId: number }> => {
        waitModal(`Submitting media data, please wait...`);
        const response = await ajaxCall("supp-acc-update-secondary-supplier", secondaryAjaxData);
        if (!response?.supplierId) {
            console.error(`saveSecondaryAjaxData - bad response:`, response);
            throw new Error(`No response.supplierId`);
        }
        return response;
    };

    const getUpdatedMainInnerHtml = async (): Promise<{ html: string, previewUrl: string }> => {
        waitModal(`Preparing preview, please wait...`);
        const response = await ajaxCall("supp-acc-get-form", { form: "edit_supplier", supplier_id: supplierId });
        if (!response?.html || !response?.previewUrl) {
            console.error(`getUpdatedMainInnerHtml - bad response:`, response);
            throw new Error(`No response.html/response.previewUrl`);
        }
        return response;
    };

    const save = async (requestApproval: boolean) => {
        const rawData = commonCtrls.gatherData();
        if (!rawData)
        {
            // User data invalid. Error modal already displayed.
            return;
        }
        const basicAjaxData = buildBasicAjaxData(rawData, requestApproval);
        if (!basicAjaxData)
        {
            throw new Error(`No basicAjaxData`);
        }
        $mainInner.addClass("busy");
        try
        {
            const basicAjaxResponse = await saveBasicAjaxData(basicAjaxData);
            if (!basicAjaxResponse?.supplierId)
            {
                throw new Error(`No basicAjaxResponse.supplierId`);
            }
            const newSupplierId: number = supplierId = basicAjaxResponse.supplierId; // set supplierId so we do post update instead of insert, in the event that there is a future error during this submission process.
            await uploadSupplierPostAttachments(rawData, newSupplierId);
            await saveSecondaryAjaxData(buildSecondaryAjaxData(rawData, newSupplierId));
            const response = await getUpdatedMainInnerHtml();
            commonCtrls.cancelWarnBeforePageLeave();
            $mainInner.html($(response.html).html());
            reinitWysiwygs($mainInner.find(`[data-wysiwyg]`));
            initCollapsibleSections();
            initCustomCheckboxes();
            initCheckboxToggle();
            initCustomSelects();
            initMainInner($mainInner, newSupplierId, response.previewUrl, requestApproval);
            scrollToWithHeaderOffset($mainInner);
        }
        catch (error)
        {
            if (!(error instanceof FileUploadItemError)) // FileUploadItemError already shows modal before throw
            {
                errorModal(`Failed to save. If the problem persists, please get in touch.`);
            }
            console.error("Save failed:", error);
            return;
        }
        finally
        {
            $mainInner.removeClass("busy");
        }
        successModal(
            requestApproval ? `Your submission has been received and is awaiting approval.` : `Your data has been saved.`,
            undefined,
            5000
        );
    };

    $save.on("click", function (event) {
        save(false);
        return false;
    });

    $publish.on("click", function (event) {
        save(true);
        return false;
    });

    if (previewUrl)
    {
        $preview.removeAttr("disabled").on("click", function (event) {
            const checkAndNavigate = async () => {
                if (unsavedChanges &&
                    !await confirmModal("Preview Profile", "You have unsaved changes which will not show up on the preview. View preview anyway?", "yesno"))
                {
                    return;
                }
                window.open(previewUrl);
            };
            checkAndNavigate();
            return false;
        });
    }
    else
    {
        $preview.attr("title", "You must save before you can preview");
    }

    if (!approvalRequested)
    {
        $publish.removeAttr("disabled");
    }
}
